import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = [
    "wrap",
    "video",
    "canvas",
    "preview",
    "hidden",
    "startButton",
    "captureButton",
    "retakeButton",
    "saveButton",
    "status",
    "progressBar",
    "flash"
  ]

  static values = {
    initialPreviewUrl: { type: String, default: "" },
    requiredOkMs: { type: Number, default: 3000 },
    minScore:     { type: Number, default: 0.8 },
    frameFill:    { type: Number, default: 0.72 },
    frameAspect:  { type: Number, default: 3 / 4 },
    centerTolX:   { type: Number, default: 0.28 },
    centerTolY:   { type: Number, default: 0.24 },
    sizeMin:      { type: Number, default: 0.42 },
    sizeMax:      { type: Number, default: 0.82 },
    onlyMobile:   { type: Boolean, default: true },
    redirectUrl:  { type: String, default: "/" },
    autoStart:    { type: Boolean, default: true },
    mediapipeBasePath: { type: String, default: "/ui/mediapipe/wasm" }
  }

  connect() {
    this.ctx = this.canvasTarget.getContext("2d")

    this.stream = null
    this.detector = null
    this.animId = null
    this.running = false
    this.hasGoodFace = false
    this.okProgressMs = 0
    this.autoShotFired = false
    this.lastFrameRect = null
    this.prevTs = null

    this.boundBeforeUnload = this.stopCamera.bind(this)
    this.boundResize = this.resizeCanvas.bind(this)

    window.addEventListener("beforeunload", this.boundBeforeUnload)
    window.addEventListener("resize", this.boundResize)

    this.resetUi()

    if (this.autoStartValue && !this.hasInitialPreview()) {
      this.boot()
    }
  }

  disconnect() {
    this.stopCamera()
    window.removeEventListener("beforeunload", this.boundBeforeUnload)
    window.removeEventListener("resize", this.boundResize)
  }

  async boot() {
    if (this.onlyMobileValue && !this.isSmartphone()) {
      this.status("Captura facial disponível apenas em smartphone.")
      alert("Esta página está disponível apenas para smartphones.")
      window.location.href = this.redirectUrlValue
      return
    }

    if (!navigator.mediaDevices?.getUserMedia) {
      this.status("Seu dispositivo não possui suporte à câmera.")
      alert("Seu dispositivo não possui suporte à câmera.")
      window.location.href = this.redirectUrlValue
      return
    }

    try {
      await this.startCamera()
    } catch (error) {
      console.error("Falha ao iniciar câmera:", error)
      this.status("Erro ao acessar câmera.")
      alert("Erro ao acessar câmera: " + error.message)
    }
  }

  async start(event) {
    event?.preventDefault()
    await this.startCamera()
  }

  captureAction(event) {
    event?.preventDefault()
    this.capture()
  }

  retakeAction(event) {
    event?.preventDefault()
    this.retake()
  }

  async initDetector() {
    const { FilesetResolver, FaceDetector } = await import("https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.20")
    const fileset = await FilesetResolver.forVisionTasks(this.mediapipeBasePathValue)

    this.detector = await FaceDetector.createFromOptions(fileset, {
      baseOptions: {
        modelAssetPath: `${this.mediapipeBasePathValue}/blaze_face_short_range.tflite`
      },
      runningMode: "VIDEO",
      minDetectionConfidence: this.minScoreValue
    })
  }

  async startCamera() {
    this.stopCamera()
    if (!this.detector) await this.initDetector()

    this.stream = await navigator.mediaDevices.getUserMedia({
      video: {
        facingMode: "user",
        width:  { ideal: 640 },
        height: { ideal: 480 }
      },
      audio: false
    })

    this.videoTarget.srcObject = this.stream
    await this.videoTarget.play()

    this.previewTarget.style.display = "none"
    this.canvasTarget.style.display = "block"
    this.captureButtonTarget.style.display = "inline-block"
    this.startButtonTarget.style.display = "none"
    this.retakeButtonTarget.style.display = "none"

    if (this.hasSaveButtonTarget) this.saveButtonTarget.disabled = true
    this.captureButtonTarget.disabled = true
    this.hiddenTarget.value = ""

    this.okProgressMs = 0
    this.autoShotFired = false
    this.hasGoodFace = false
    this.prevTs = null

    this.resizeCanvas()
    this.status("Alinhe o rosto ao círculo.")
    this.setProgress(0)
    this.runLoop()
  }

  stopCamera() {
    this.running = false

    if (this.animId) {
      cancelAnimationFrame(this.animId)
      this.animId = null
    }

    if (this.stream) {
      this.stream.getTracks().forEach((track) => track.stop())
      this.stream = null
    }
  }

  resetUi() {
    const hasInitialPreview = this.hasInitialPreview()

    this.previewTarget.style.display = hasInitialPreview ? "block" : "none"
    this.canvasTarget.style.display = hasInitialPreview ? "none" : "block"
    this.startButtonTarget.style.display = hasInitialPreview || this.autoStartValue ? "none" : "inline-block"
    this.captureButtonTarget.style.display = hasInitialPreview ? "none" : "inline-block"
    this.retakeButtonTarget.style.display = hasInitialPreview ? "inline-block" : "none"

    this.captureButtonTarget.disabled = true
    if (this.hasSaveButtonTarget) this.saveButtonTarget.disabled = true

    this.hiddenTarget.value = ""
    this.setProgress(0)
    this.status(hasInitialPreview ? "Imagem atual." : "Inicializando câmera...")
  }

  resizeCanvas() {
    if (!this.hasWrapTarget || !this.hasCanvasTarget) return

    const rect = this.wrapTarget.getBoundingClientRect()
    if (rect.width === 0) return

    const cssHeight = rect.width / this.frameAspectValue
    this.wrapTarget.style.height = `${cssHeight}px`

    const dpr = window.devicePixelRatio || 1
    this.canvasTarget.width = rect.width * dpr
    this.canvasTarget.height = cssHeight * dpr
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
  }

  runLoop() {
    if (this.running) return
    this.running = true

    const step = async () => {
      if (!this.running) return

      if (!this.videoTarget || this.videoTarget.readyState < 2) {
        this.animId = requestAnimationFrame(step)
        return
      }

      const rect = this.canvasTarget.getBoundingClientRect()
      const W = rect.width
      const H = rect.height
      const cover = this.getVideoCoverRect(W, H)

      this.ctx.clearRect(0, 0, W, H)
      this.ctx.drawImage(
        this.videoTarget,
        cover.dx,
        cover.dy,
        cover.drawW,
        cover.drawH
      )

      await this.onFrame(performance.now(), W, H, cover)

      this.animId = requestAnimationFrame(step)
    }

    this.animId = requestAnimationFrame(step)
  }

  async onFrame(ts, W, H, cover) {
    const F = this.getPortraitFrame(W, H)
    this.lastFrameRect = F

    this.drawGuide(F, W, H)

    let box = null
    let faceCount = 0

    try {
      const out = await this.detector.detectForVideo(this.videoTarget, ts)

      faceCount = out?.detections?.length || 0
      if (faceCount === 1) {
        const b = out.detections[0].boundingBox

        box = {
          x: cover.dx + (b.originX * cover.scale),
          y: cover.dy + (b.originY * cover.scale),
          w: b.width * cover.scale,
          h: b.height * cover.scale
        }
      }
    } catch (_) {
      // falha silenciosa da detecção em um frame específico
    }

    if (!this.prevTs) this.prevTs = ts
    const dt = Math.max(16, Math.min(50, ts - this.prevTs))
    this.prevTs = ts

    let ok = false

    if (box) {
      const faceCx = box.x + box.w / 2
      const faceCy = box.y + box.h / 2

      const dX = Math.abs(faceCx - F.cx) / (F.w / 2)
      const dY = Math.abs(faceCy - F.cy) / (F.h / 2)

      const fitX = box.w / F.w
      const fitY = box.h / F.h
      const marginX = F.w * 0.04
      const marginY = F.h * 0.04

      const centered = dX < this.centerTolXValue && dY < this.centerTolYValue
      const fullyVisible =
        box.x >= F.x + marginX &&
        box.y >= F.y + marginY &&
        box.x + box.w <= F.x + F.w - marginX &&
        box.y + box.h <= F.y + F.h - marginY
      const goodSize =
        fitX > this.sizeMinValue &&
        fitX < this.sizeMaxValue &&
        fitY > this.sizeMinValue &&
        fitY < this.sizeMaxValue

      ok = centered && fullyVisible && goodSize

      if (ok) {
        this.status("Rosto OK! Fique parado…")
        this.okProgressMs = Math.min(this.requiredOkMsValue, this.okProgressMs + dt)
      } else {
        this.okProgressMs = Math.max(0, this.okProgressMs - dt)

        if (!centered) {
          this.status("Posicione o rosto dentro do círculo e olhe para a câmera.")
        } else if (!fullyVisible || fitX >= this.sizeMaxValue || fitY >= this.sizeMaxValue) {
          this.status("Afaste um pouco e mantenha toda a cabeça visível.")
        } else if (fitX <= this.sizeMinValue || fitY <= this.sizeMinValue) {
          this.status("Aproxime um pouco.")
        } else {
          this.status("Ajuste a posição.")
        }
      }

      if (ok) {
        const remain = Math.max(0, this.requiredOkMsValue - this.okProgressMs)
        const num = Math.ceil(remain / 1000)

        if (num > 0) {
          this.ctx.save()
          this.ctx.font = `bold ${Math.round(Math.min(W, H) * 0.20)}px system-ui`
          this.ctx.fillStyle = "rgba(255,255,255,.95)"
          this.ctx.textAlign = "center"
          this.ctx.textBaseline = "middle"
          this.ctx.shadowColor = "rgba(0,0,0,.6)"
          this.ctx.shadowBlur = 8
          this.ctx.fillText(String(num), W / 2, H * 0.35)
          this.ctx.restore()
        }
      }
    } else if (faceCount > 1) {
      this.okProgressMs = Math.max(0, this.okProgressMs - 120)
      this.status("Deixe apenas uma pessoa visível na câmera.")
    } else {
      this.okProgressMs = Math.max(0, this.okProgressMs - 120)
      this.status("Detectando rosto…")
    }

    this.hasGoodFace = ok
    this.captureButtonTarget.disabled = !ok
    this.setProgress(this.okProgressMs)

    if (this.okProgressMs >= this.requiredOkMsValue && !this.autoShotFired) {
      this.autoShotFired = true
      this.flashEffect()
      setTimeout(() => this.capture(), 300)
    }
  }

  drawGuide(F, W, H) {
    this.ctx.save()
    this.ctx.fillStyle = "rgba(0,0,0,.35)"
    this.ctx.fillRect(0, 0, W, H)
    this.ctx.globalCompositeOperation = "destination-out"
    this.ctx.beginPath()
    this.ctx.ellipse(F.cx, F.cy, F.rx, F.ry, 0, 0, Math.PI * 2)
    this.ctx.fill()
    this.ctx.restore()

    this.ctx.save()
    this.ctx.lineWidth = Math.max(3, Math.round(Math.min(W, H) * 0.018))
    this.ctx.strokeStyle = this.hasGoodFace
      ? "rgba(34,197,94,.95)"
      : "rgba(255,0,0,.95)"
    this.ctx.beginPath()
    this.ctx.ellipse(F.cx, F.cy, F.rx, F.ry, 0, 0, Math.PI * 2)
    this.ctx.stroke()
    this.ctx.restore()
  }

  setProgress(ms) {
    const pct = Math.max(0, Math.min(100, (ms / this.requiredOkMsValue) * 100))
    this.progressBarTarget.style.width = `${pct}%`
  }

  capture() {
    const rect = this.canvasTarget.getBoundingClientRect()
    const F = this.lastFrameRect || this.getPortraitFrame(rect.width, rect.height)
    const cover = this.getVideoCoverRect(rect.width, rect.height)
    const videoW = this.videoTarget.videoWidth
    const videoH = this.videoTarget.videoHeight

    let srcX = Math.round((F.x - cover.dx) / cover.scale)
    let srcY = Math.round((F.y - cover.dy) / cover.scale)
    let srcW = Math.round(F.w / cover.scale)
    let srcH = Math.round(F.h / cover.scale)

    srcX = Math.max(0, Math.min(videoW - 1, srcX))
    srcY = Math.max(0, Math.min(videoH - 1, srcY))
    srcW = Math.max(1, Math.min(videoW - srcX, srcW))
    srcH = Math.max(1, Math.min(videoH - srcY, srcH))

    const TARGET_W = 360
    const TARGET_H = Math.round(TARGET_W / this.frameAspectValue)

    const out = document.createElement("canvas")
    out.width = TARGET_W
    out.height = TARGET_H

    const octx = out.getContext("2d")

    octx.translate(out.width, 0)
    octx.scale(-1, 1)

    octx.drawImage(
      this.videoTarget,
      srcX, srcY, srcW, srcH,
      0, 0, out.width, out.height
    )

    const dataUrl = out.toDataURL("image/jpeg", 0.92)

    this.previewTarget.src = dataUrl
    this.previewTarget.style.display = "block"
    this.canvasTarget.style.display = "none"

    this.captureButtonTarget.style.display = "none"
    this.retakeButtonTarget.style.display = "inline-block"

    this.hiddenTarget.value = dataUrl
    if (this.hasSaveButtonTarget) this.saveButtonTarget.disabled = false

    this.stopCamera()
    this.status("Imagem pronta.")
  }

  retake() {
    if (this.hasSaveButtonTarget) this.saveButtonTarget.disabled = true
    this.hiddenTarget.value = ""
    this.startCamera()
  }

  flashEffect() {
    this.flashTarget.classList.add("active")
    setTimeout(() => this.flashTarget.classList.remove("active"), 150)
  }

  getPortraitFrame(W, H) {
    const maxW = W * this.frameFillValue
    const maxH = H * this.frameFillValue

    const rectW = Math.min(maxW, maxH * this.frameAspectValue)
    const rectH = rectW / this.frameAspectValue

    const cx = W / 2
    const cy = H * 0.50
    const x = Math.round(cx - rectW / 2)
    const y = Math.round(cy - rectH / 2)

    return {
      x,
      y,
      w: Math.round(rectW),
      h: Math.round(rectH),
      cx,
      cy,
      rx: rectW / 2,
      ry: rectH / 2
    }
  }

  status(text) {
    this.statusTarget.textContent = text
  }

  isSmartphone() {
    return /Android.*Mobile|iPhone|iPod|Windows Phone|BlackBerry|Opera Mini|IEMobile/i.test(navigator.userAgent)
  }

  hasInitialPreview() {
    return this.initialPreviewUrlValue.trim().length > 0
  }

  getVideoCoverRect(destW, destH) {
    const videoW = this.videoTarget.videoWidth || 1
    const videoH = this.videoTarget.videoHeight || 1
    const scale = Math.max(destW / videoW, destH / videoH)
    const drawW = videoW * scale
    const drawH = videoH * scale

    return {
      scale,
      drawW,
      drawH,
      dx: (destW - drawW) / 2,
      dy: (destH - drawH) / 2
    }
  }
}
